/**
 * Side Pane — search, tag filter, and the authoring assistant, as sibling tabs.
 * Always visible for desktop authors on the opposite side of the chapter sidebar.
 *
 * The assistant lives here rather than in a drawer so a conversation survives while the
 * author reads the manuscript it is talking about. Its tab is wider than search, because
 * candidate cards carry real prose.
 */

import { Box, Popper, Tab, Tabs } from '@mui/material'
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import AssistantChat from '../../pages/book-editor/components/AssistantChat'
import type { ApplyProposalAction } from '../../pages/book-editor/components/ContentProposalReview'
import { SearchResult } from '../../services/api'
import SearchPane, { SearchPaneHandle } from '../search/SearchPane'
import { TagDefinition } from '../tags/tagTreeUtils'

export interface SidePaneHandle {
  focusSearch: () => void
  /** Bring the assistant tab forward — how a marked passage reaches it from the editor. */
  showAssistant: () => void
}

interface SidePaneProps {
  onSearchNavigate: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  onSearchPreview?: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  onSearchStepSelection?: (direction: -1 | 1) => void
  onSearchEnterSelection?: () => void
  selectedSearchResultKey?: string | null
  searchQuery: string
  searchResults: SearchResult[]
  searchLoading: boolean
  searchSearched: boolean
  direction?: 'ltr' | 'rtl'
  onSearchQueryChange: (query: string) => void
  onSearchClear: () => void
  showPreviewPopup?: boolean
  previewPopupPlacement?: 'left-start' | 'right-start'
  previewPopupContent?: React.ReactNode
  // Tag filter props
  selectedTagIds?: string[]
  onTagsChange?: (tagIds: string[]) => void
  tags?: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  tagUsageCounts?: Record<string, number>
  onOpenTagManager?: () => void
  isPreviewOpen?: boolean
  onClosePreview?: () => void
  width?: number | string
  /** Book id enables the assistant tab; omitted, the pane is search-only. */
  bookId?: string
  assistantAvailable?: boolean
  /** What the author is looking at, passed through to the assistant for context. */
  assistantFilename?: string
  assistantStartBlockId?: string
  assistantMarkedText?: string
  onApplyProposal?: ApplyProposalAction
  onGoTo?: (filename: string, blockId: string, text: string) => void
  /** Wider while the assistant tab is showing — candidate cards need the room. */
  assistantWidth?: number | string
}

const SidePane = forwardRef<SidePaneHandle, SidePaneProps>(
  (
    {
      onSearchNavigate,
      onSearchPreview,
      onSearchStepSelection,
      onSearchEnterSelection,
      selectedSearchResultKey,
      searchQuery,
      searchResults,
      searchLoading,
      searchSearched,
      direction = 'ltr',
      onSearchQueryChange,
      onSearchClear,
      showPreviewPopup = false,
      previewPopupPlacement = 'left-start',
      previewPopupContent,
      selectedTagIds,
      onTagsChange,
      tags,
      tagColorMap,
      tagUsageCounts,
      onOpenTagManager,
      isPreviewOpen,
      onClosePreview,
      width = 240,
      bookId,
      assistantAvailable = false,
      assistantFilename,
      assistantStartBlockId,
      assistantMarkedText,
      onGoTo,
      onApplyProposal,
      assistantWidth = 380,
    },
    ref,
  ) => {
    const searchRef = useRef<SearchPaneHandle>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const [tab, setTab] = useState<'search' | 'assistant'>('search')

    const showAssistant = assistantAvailable && !!bookId
    const activeTab = showAssistant ? tab : 'search'

    useImperativeHandle(ref, () => ({
      focusSearch: () => {
        setTab('search')
        setTimeout(() => searchRef.current?.focusInput(), 50)
      },
      showAssistant: () => setTab('assistant'),
    }))

    return (
      <Box
        ref={rootRef}
        sx={{
          width: activeTab === 'assistant' ? assistantWidth : width,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--bg-surface)',
          borderInlineStart: '1px solid var(--border-subtle)',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        {showAssistant && (
          <Tabs
            value={activeTab}
            onChange={(_event, next) => setTab(next)}
            variant="fullWidth"
            sx={{ minHeight: 38, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="search" label="חיפוש" sx={{ minHeight: 38, py: 0 }} />
            <Tab value="assistant" label="שיחה" sx={{ minHeight: 38, py: 0 }} />
          </Tabs>
        )}

        {/*
          Kept mounted rather than conditionally rendered: unmounting throws away the
          conversation, and a corpus answer costs real money and a minute of waiting.
        */}
        {showAssistant && (
          <Box
            sx={{
              display: activeTab === 'assistant' ? 'flex' : 'none',
              flexDirection: 'column',
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            <AssistantChat
              onApplyProposal={onApplyProposal}
              bookId={bookId}
              filename={assistantFilename}
              startBlockId={assistantStartBlockId}
              markedText={assistantMarkedText}
              direction={direction}
              onGoTo={onGoTo}
            />
          </Box>
        )}

        <Box
          sx={{
            display: activeTab === 'search' ? 'flex' : 'none',
            flexDirection: 'column',
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <SearchPane
            ref={searchRef}
            query={searchQuery}
            results={searchResults}
            loading={searchLoading}
            searched={searchSearched}
            direction={direction}
            onQueryChange={onSearchQueryChange}
            onClear={onSearchClear}
            onNavigate={onSearchNavigate}
            onPreviewSelect={onSearchPreview}
            onStepSelection={onSearchStepSelection}
            onEnterSelection={onSearchEnterSelection}
            selectedResultKey={selectedSearchResultKey}
            selectedTagIds={selectedTagIds}
            onTagsChange={onTagsChange}
            tags={tags}
            tagColorMap={tagColorMap}
            tagUsageCounts={tagUsageCounts}
            onOpenTagManager={onOpenTagManager}
            isPreviewOpen={isPreviewOpen}
            onClosePreview={onClosePreview}
          />
        </Box>
        {previewPopupContent && (
          <Popper
            open={showPreviewPopup}
            anchorEl={rootRef.current}
            placement={previewPopupPlacement}
            modifiers={[
              { name: 'offset', options: { offset: [0, 12] } },
              { name: 'preventOverflow', options: { padding: 16 } },
            ]}
            sx={{ zIndex: 1400 }}
          >
            {previewPopupContent}
          </Popper>
        )}
      </Box>
    )
  },
)

SidePane.displayName = 'SidePane'

export default SidePane
